<template>
  <v-dialog v-model="dialog" max-width="800" persistent scrollable>
    <v-card>
      <v-card-title class="text-h5 primary white--text">
        <v-icon left dark>mdi-gavel</v-icon>
        {{ isEdit ? "Edit" : "Create" }} Case File
      </v-card-title>

      <v-card-text class="pt-6">
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="formData.title"
                label="Case Title"
                :rules="[rules.required]"
                outlined
                dense
              >
                <template v-slot:prepend>
                  <v-icon>mdi-file-document</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.case_number"
                label="Case Number"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.date_opened"
                label="Date Opened"
                type="date"
                :rules="[rules.required]"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.petitioner"
                label="Petitioner/Plaintiff"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.respondent"
                label="Respondent/Defendant"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="formData.case_type"
                :items="caseTypes"
                label="Case Type"
                outlined
                dense
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="formData.status"
                :items="statusOptions"
                label="Status"
                outlined
                dense
              ></v-select>
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="formData.court_id"
                :items="courts"
                item-text="name"
                item-value="id"
                label="Court"
                outlined
                dense
                clearable
              ></v-autocomplete>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.judge"
                label="Judge/Magistrate"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.description"
                label="Case Description"
                outlined
                rows="4"
              ></v-textarea>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.notes"
                label="Additional Notes"
                outlined
                rows="3"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog"> Cancel </v-btn>
        <v-btn color="primary" @click="saveCase" :disabled="!valid">
          <v-icon left>mdi-content-save</v-icon>
          Save Case
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "AddCaseDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    caseFile: {
      type: Object,
      default: null,
    },
    clientId: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    valid: false,
    formData: {
      title: "",
      case_number: "",
      date_opened: new Date().toISOString().substr(0, 10),
      petitioner: "",
      respondent: "",
      case_type: "",
      status: "active",
      court_id: null,
      judge: "",
      description: "",
      notes: "",
    },
    caseTypes: [
      "Civil",
      "Criminal",
      "Family",
      "Commercial",
      "Labor",
      "Constitutional",
      "Administrative",
      "Other",
    ],
    statusOptions: [
      { text: "Active", value: "active" },
      { text: "Pending", value: "pending" },
      { text: "On Hold", value: "on_hold" },
      { text: "Completed", value: "completed" },
      { text: "Closed", value: "closed" },
    ],
    courts: [],
    rules: {
      required: (v) => !!v || "This field is required",
    },
  }),

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    isEdit() {
      return this.caseFile !== null;
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadCourts();
        if (this.caseFile) {
          this.formData = { ...this.caseFile };
        } else {
          this.resetForm();
        }
      }
    },
  },

  methods: {
    async loadCourts() {
      // Load courts from API
      try {
        const response = await this.$axios.getData(
          "/auth/get-courts",
          this.$store.getters.getAccessToken
        );
        this.courts = response;
      } catch (error) {
        console.error("Error loading courts:", error);
      }
    },

    saveCase() {
      if (this.$refs.form.validate()) {
        this.$emit("save", {
          ...this.formData,
          client_id: this.clientId,
        });
        this.closeDialog();
      }
    },

    closeDialog() {
      this.$emit("input", false);
      this.$emit("close");
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        title: "",
        case_number: "",
        date_opened: new Date().toISOString().substr(0, 10),
        petitioner: "",
        respondent: "",
        case_type: "",
        status: "active",
        court_id: null,
        judge: "",
        description: "",
        notes: "",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>
